import { absoluteUrl, SITE_URL } from '@/seo/jsonld';
import type { RouteSeo } from '@/seo/routes';

/**
 * O `<head>` de cada página, montado a partir do registro de rotas.
 *
 * Duas saídas para o mesmo conteúdo:
 * - `headTags`: texto HTML, gravado no arquivo de cada rota durante o build;
 * - `applyHeadToDocument`: atualiza o documento quando o visitante navega sem
 *   recarregar a página.
 *
 * As duas leem `HeadData`, então título, descrição e canonical nunca divergem
 * entre o que o robô recebeu e o que a aba mostra.
 */
export const OG_IMAGE = `${SITE_URL}/og-site.png`;

/**
 * Imagem de compartilhamento de cada página (`public/og/<nome>.jpg`, 1200x630),
 * gerada por `scripts/og/generate.mjs`. Página sem imagem própria cai na do
 * site. Rotas filhas (um app, um setor, um guia, um caso) herdam a da seção.
 */
const OG_BY_SECTION: Record<string, string> = {
  '/': 'home',
  '/assinar': 'assinar',
  '/solicitar-orcamento': 'contato',
  '/apps': 'apps',
  '/clientes': 'clientes',
  '/casos': 'casos',
  '/precos': 'precos',
  '/produto': 'produto',
  '/setores': 'setores',
  '/guias': 'guias',
  '/sobre': 'sobre',
  '/contato': 'contato',
  '/calculadora': 'calculadora',
};

export function ogImageForPath(path: string): string {
  const section = path === '/' ? '/' : `/${path.split('/').filter(Boolean)[0] ?? ''}`;
  const name = OG_BY_SECTION[section];
  return name ? `${SITE_URL}/og/${name}.jpg` : OG_IMAGE;
}
const LD_SCRIPT_ID = 'operax-ld';

export interface HeadData {
  title: string;
  description: string;
  canonical: string;
  noindex: boolean;
  ogImage?: string;
  jsonLd?: unknown;
}

export function headDataFromRoute(route: RouteSeo): HeadData {
  return {
    title: route.title,
    description: route.description,
    canonical: absoluteUrl(route.path),
    noindex: Boolean(route.noindex),
    ogImage: ogImageForPath(route.path),
    jsonLd: route.jsonLd,
  };
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** JSON dentro de `<script>`: `<` escapado impede fechar a tag por conteúdo. */
function safeJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

export function headTags(head: HeadData): string {
  const tags = [
    `<title>${escapeAttr(head.title)}</title>`,
    `<meta name="description" content="${escapeAttr(head.description)}" />`,
    `<link rel="canonical" href="${escapeAttr(head.canonical)}" />`,
    `<meta name="robots" content="${head.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1'}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="Operax" />`,
    `<meta property="og:locale" content="pt_BR" />`,
    `<meta property="og:title" content="${escapeAttr(head.title)}" />`,
    `<meta property="og:description" content="${escapeAttr(head.description)}" />`,
    `<meta property="og:url" content="${escapeAttr(head.canonical)}" />`,
    `<meta property="og:image" content="${escapeAttr(head.ogImage ?? OG_IMAGE)}" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeAttr(head.title)}" />`,
    `<meta name="twitter:description" content="${escapeAttr(head.description)}" />`,
    `<meta name="twitter:image" content="${escapeAttr(head.ogImage ?? OG_IMAGE)}" />`,
  ];
  if (head.jsonLd) {
    tags.push(`<script type="application/ld+json" id="${LD_SCRIPT_ID}">${safeJson(head.jsonLd)}</script>`);
  }
  return tags.join('\n    ');
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function applyHeadToDocument(head: HeadData): void {
  document.title = head.title;
  setMeta('name', 'description', head.description);
  setCanonical(head.canonical);
  setMeta('name', 'robots', head.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1');
  setMeta('property', 'og:title', head.title);
  setMeta('property', 'og:description', head.description);
  setMeta('property', 'og:url', head.canonical);
  setMeta('name', 'twitter:title', head.title);
  setMeta('name', 'twitter:description', head.description);
  setMeta('property', 'og:image', head.ogImage ?? OG_IMAGE);
  setMeta('name', 'twitter:image', head.ogImage ?? OG_IMAGE);

  const existing = document.getElementById(LD_SCRIPT_ID);
  if (!head.jsonLd) {
    existing?.remove();
    return;
  }
  const script = existing ?? document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.id = LD_SCRIPT_ID;
  script.textContent = JSON.stringify(head.jsonLd);
  if (!existing) document.head.appendChild(script);
}
